import React from 'react'
import { createPortal } from 'react-dom'
import { IntelligentSearchResult } from '../services/intelligentSearchService'
import './IntelligentSearchModal.css'
import './CompanyIntelligenceCategoryModal.css'

interface CompanyIntelligenceCategoryModalProps {
  isOpen: boolean
  onClose: () => void
  categoryLabel: string
  companyName?: string | null
  query: string
  results: IntelligentSearchResult | null
  isLoading: boolean
  error: string | null
  onRefresh?: () => void
}

export const CompanyIntelligenceCategoryModal: React.FC<CompanyIntelligenceCategoryModalProps> = ({
  isOpen,
  onClose,
  categoryLabel,
  companyName,
  query,
  results,
  isLoading,
  error,
  onRefresh,
}) => {
  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || typeof document === 'undefined' || !document.body) {
    return null
  }

  const effectiveQuery = results?.searchQuery || query

  const panel = (
    <>
      <div
        className="intelligent-search-panel-overlay company-intel-category-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onClose()
          }
        }}
      />
      <div className="intelligent-search-panel company-intel-category-panel">
        <div className="intelligent-search-panel-header company-intel-category-header">
          <h2 className="intelligent-search-panel-title">
            Deep Dive: {categoryLabel}
            {companyName && (
              <span className="intelligent-search-query-badge">{companyName}</span>
            )}
          </h2>
          <div className="company-intel-category-actions">
            {onRefresh && (
              <button
                className="company-intel-category-refresh"
                onClick={onRefresh}
                disabled={isLoading}
                title="Refresh deep dive"
                aria-label="Refresh deep dive"
              >
                {isLoading ? '⏳' : '🔄'}
              </button>
            )}
            <button
              className="intelligent-search-panel-close"
              onClick={onClose}
              aria-label="Close deep dive"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="intelligent-search-panel-body">
          {isLoading && (
            <div className="intelligent-search-loading">
              <div className="intelligent-search-spinner"></div>
              <p>Our AI is combing the web for the insights you asked for... This usually takes about 30-60 seconds.</p>
            </div>
          )}

          {error && (
            <div className="intelligent-search-error">
              <p className="error-message">{error}</p>
            </div>
          )}

          {!isLoading && !error && (
            <div className="intelligent-search-results">
              {effectiveQuery && (
                <div className="intelligent-search-summary">
                  <h3>Search Query</h3>
                  <p>{effectiveQuery}</p>
                </div>
              )}

              {results?.summary && (
                <div className="intelligent-search-summary">
                  <h3>Summary</h3>
                  <p>{results.summary}</p>
                </div>
              )}

              {results?.results && results.results.length > 0 ? (
                <div className="intelligent-search-results-list">
                  <h3>Sources ({results.results.length})</h3>
                  <ul>
                    {results.results.map((result, index) => {
                      const resultKey = result.url || `${index}-${result.title || 'result'}`
                      return (
                        <li key={resultKey} className="intelligent-search-result-item">
                          <div className="intelligent-search-result-content">
                            <a
                              href={result.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="intelligent-search-result-link"
                            >
                              <h4>{result.title || 'Untitled'}</h4>
                              <p className="intelligent-search-result-snippet">
                                {result.snippet || 'No description available'}
                              </p>
                            </a>
                            {result.url && (
                              <a
                                href={result.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="intelligent-search-job-link"
                                title={result.url}
                              >
                                Open source
                              </a>
                            )}
                            {result.relevance !== undefined && (
                              <span className="intelligent-search-relevance">
                                Relevance: {result.relevance}%
                              </span>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ) : (
                <div className="intelligent-search-empty">
                  <p>No results found. Try adjusting the query or refresh.</p>
                </div>
              )}

              {results?.citations && results.citations.length > 0 && (
                <div className="intelligent-search-citations">
                  <h3>Citations</h3>
                  <ul>
                    {results.citations.map((citation) => (
                      <li key={citation}>
                        <a
                          href={citation}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="intelligent-search-citation-link"
                        >
                          {citation}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return createPortal(panel, document.body)
}
